import React from 'react';
import EChart from '../charts/EChart';
import LazyMount from '../charts/LazyMount';
import { trendOption } from '../charts/chartOptions';

// Shared presentational primitives for dashboard cards/charts. Each takes an
// `isDark` prop because the app derives dark mode per-component (localStorage
// 'darkMode' poll) rather than from a theme context.

const surface = (isDark) =>
  isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';

export const KpiCard = ({ label, value, sub, color, isDark, className = '' }) => (
  <div className={`rounded-xl border px-3 py-2 ${surface(isDark)} ${className}`}>
    <div
      className={`text-lg font-bold leading-tight tabular-nums ${
        color || (isDark ? 'text-white' : 'text-gray-900')
      }`}
    >
      {value ?? 0}
    </div>
    <div className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
      {label}
    </div>
    {sub != null && (
      <div className={`text-[11px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{sub}</div>
    )}
  </div>
);

export const ChartCard = ({ title, subtitle, actions, children, isDark, style, className = '', bodyClassName = '' }) => (
  <div style={style} className={`rounded-xl shadow-lg border p-6 ${surface(isDark)} ${className}`}>
    {title && (
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
          {subtitle && (
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{subtitle}</p>
          )}
        </div>
        {actions}
      </div>
    )}
    <div className={bodyClassName}>{children}</div>
  </div>
);

export const EmptyState = ({ message, hint, isDark, className = '' }) => (
  <div className={`py-12 text-center ${className}`}>
    <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{message}</p>
    {hint && <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{hint}</p>}
  </div>
);

export const StatusPill = ({ label, value, colorClass, isDark, className = '' }) => (
  <span
    className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${
      colorClass || (isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700')
    } ${className}`}
  >
    {value == null ? label : `${label}: ${value}`}
  </span>
);

export const SkeletonBlock = ({ isDark, className = '' }) => (
  <div className={`animate-pulse rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-200'} ${className}`} />
);

// Shared search input: leading icon + inline clear. One look across the app.
export const SearchInput = ({ value, onChange, placeholder = 'Search…', onClear, isDark, className = '', name, id }) => (
  <div className={`relative ${className}`}>
    <svg
      className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
      fill="none" stroke="currentColor" viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    <input
      type="text"
      name={name}
      id={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full pl-9 pr-8 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
        isDark
          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
          : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'
      }`}
    />
    {value && (
      <button
        type="button"
        onClick={onClear}
        title="Clear"
        aria-label="Clear search"
        className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    )}
  </div>
);

// Shared segmented tab switcher (optional count badge per tab).
export const SegmentedTabs = ({ options = [], value, onChange, isDark, size = 'md', className = '' }) => {
  const pad = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm';
  return (
    <div className={`inline-flex items-center rounded-lg border p-0.5 gap-0.5 ${isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-gray-50'} ${className}`}>
      {options.map(o => {
        const active = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            className={`${pad} rounded-md font-medium whitespace-nowrap transition-colors ${
              active ? 'bg-indigo-600 text-white shadow-sm' : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {o.label}
            {o.badge != null && (
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                active ? 'bg-white/25 text-white' : isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
              }`}>
                {o.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// Shared trend chart card: area line + value labels + day-over-day tooltip,
// with a compact total/last-day summary and an empty state.
export const TrendCard = ({
  title,
  subtitle,
  labels = [],
  data = [],
  color,
  isDark,
  style,
  valueFormatter = v => v,
  unit = '',
  height = '250px',
  summaryNoun = 'total',
  emptyMessage = 'No data for the last 7 days',
}) => {
  const nums = (data || []).map(v => Number(v) || 0);
  const total = nums.reduce((s, v) => s + v, 0);
  const last = nums.length ? nums[nums.length - 1] : null;
  const prev = nums.length > 1 ? nums[nums.length - 2] : null;
  const delta = last != null && prev != null ? last - prev : null;
  const actions = nums.length ? (
    <div className="text-right leading-tight">
      <div className={`text-lg font-bold tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {valueFormatter(total)}{unit}
      </div>
      <div className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {summaryNoun}
        {delta != null && (
          <span className={delta >= 0 ? 'text-green-500' : 'text-red-500'}>
            {' '}· {delta >= 0 ? '+' : ''}{valueFormatter(delta)} today
          </span>
        )}
      </div>
    </div>
  ) : null;
  return (
    <ChartCard title={title} subtitle={subtitle} actions={actions} isDark={isDark} style={style}>
      {labels.length > 0 ? (
        <LazyMount style={{ height }}>
          <EChart option={trendOption({ labels, data, color, isDark, valueFormatter, unit })} />
        </LazyMount>
      ) : (
        <EmptyState message={emptyMessage} isDark={isDark} className="py-16" />
      )}
    </ChartCard>
  );
};
